import React, { useState, useEffect } from "react";
import { GET_PRODUCTS_BY_CATEGORY } from "../utils/constant";
import API_CLIENT from "../lib/api-client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faArrowRight, 
  faArrowLeft,
  faCartShopping
} from "@fortawesome/free-solid-svg-icons";
import { Link, useNavigate } from "react-router-dom";
import { useCartStore } from "../store/slice/cartSlice";
import { toast } from "react-hot-toast";

const RelatedProduct = ({ id }) => {
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { addToCart } = useCartStore();
  
  // For pagination
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 3; // Show 3 products per page to match the design
  
  useEffect(() => {
    const fetchRelatedProducts = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const response = await API_CLIENT.get(GET_PRODUCTS_BY_CATEGORY + id);
        if (response.data.success && response.data.products) {
          setRelatedProducts(response.data.products.slice(0, 12)); // Limit to 12 products max
        }
      } catch (error) {
        console.error("Error fetching related products:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRelatedProducts();
  }, [id]);

  // Navigation handlers
  const goToNextPage = () => {
    if (currentPage < Math.ceil(relatedProducts.length / productsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Get current products to display
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = relatedProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  
  // Handle cart action
  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success(`${product.name} added to cart`);
  };

  if (loading) {
    return (
      <div className="py-16 text-center">
        <div className="inline-block w-8 h-8 border-2 border-gray-200 border-t-teal-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (relatedProducts.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        {/* Header with title and navigation */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">You Might Also Like</h2>
            <p className="text-gray-500 mt-1">Discover more products in this collection</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Link 
              to={`/category/${id}`}
              className="text-teal-600 hover:text-teal-700 font-medium flex items-center"
            >
              View All
              <FontAwesomeIcon icon={faArrowRight} className="ml-1 text-xs" />
            </Link>
            
            {relatedProducts.length > productsPerPage && (
              <div className="flex gap-1 ml-4">
                <button 
                  onClick={goToPrevPage}
                  disabled={currentPage === 1}
                  className={`w-8 h-8 rounded-full flex items-center justify-center border
                    ${currentPage === 1 
                      ? 'border-gray-200 text-gray-300' 
                      : 'border-gray-300 text-gray-700 hover:border-teal-500 hover:text-teal-500'}`}
                  aria-label="Previous page"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="text-xs" />
                </button>
                
                <button 
                  onClick={goToNextPage}
                  disabled={currentPage >= Math.ceil(relatedProducts.length / productsPerPage)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center border
                    ${currentPage >= Math.ceil(relatedProducts.length / productsPerPage) 
                      ? 'border-gray-200 text-gray-300' 
                      : 'border-gray-300 text-gray-700 hover:border-teal-500 hover:text-teal-500'}`}
                  aria-label="Next page"
                >
                  <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Product grid - fixed width cards that look good with any number of products */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentProducts.map(product => {
            // Calculate price with discount
            const discountedPrice = product.price * (1 - product.discount / 100);
            
            return (
              <div key={product._id} className="group relative bg-black rounded-xl overflow-hidden">
                {/* Product image */}
                <div className="aspect-[16/9] overflow-hidden">
                  <img 
                    src={product.images[0]} 
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-700 
                      group-hover:scale-105"
                  />
                </div>
                
                {/* Badges */}
                {product.discount > 0 && (
                  <div className="absolute top-3 left-3 bg-red-500 text-white px-2 py-1 text-xs font-semibold rounded-sm">
                    -{product.discount}%
                  </div>
                )}
                
                {product.newProduct && (
                  <div className="absolute top-3 left-3 bg-teal-500 text-white px-2 py-1 text-xs font-semibold rounded-sm">
                    NEW
                  </div>
                )}
                
                {/* Product info */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-5 text-white">
                  <h3 className="text-lg font-semibold mb-1">{product.name}</h3>
                  <p className="text-sm text-gray-200 line-clamp-2 mb-3">{product.description}</p>
                  
                  {/* Price and action button */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline">
                      <span className="text-lg font-bold">₹{discountedPrice.toLocaleString('en-IN')}</span>
                      {product.discount > 0 && (
                        <span className="ml-2 text-sm text-gray-300 line-through">
                          ₹{product.price.toLocaleString('en-IN')}
                        </span>
                      )}
                    </div>
                    
                    <button
                      onClick={() => navigate(`/product/${product._id}`)}
                      className="px-3 py-1.5 bg-white text-black text-sm font-medium rounded-full
                        hover:bg-teal-500 hover:text-white transition-colors duration-300"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Pagination dots for mobile */}
        {relatedProducts.length > productsPerPage && (
          <div className="flex justify-center gap-1.5 mt-8">
            {[...Array(Math.ceil(relatedProducts.length / productsPerPage))].map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPage(index + 1)}
                className={`w-2 h-2 rounded-full transition-all duration-300 
                  ${currentPage === index + 1 ? 'bg-teal-600 w-6' : 'bg-gray-300'}`}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default RelatedProduct;
